import React, { useState, useEffect } from 'react';
import researcherApi from '../../services/researcherApi';

const SandboxPersonaFilter = ({ token, personaType, onCloneSandbox }) => {
  const [researchers, setResearchers] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [sandboxPersonas, setSandboxPersonas] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [cloningId, setCloningId] = useState(null);
  const [cloneResult, setCloneResult] = useState(null); // { id, success, message }
  const [cloneForm, setCloneForm] = useState(null); // { personaId, key, name, persona }

  // Fetch researchers on mount
  useEffect(() => {
    if (!token) return;
    const fetchResearchers = async () => {
      try {
        const data = await researcherApi.listResearchers();
        setResearchers(data || []);
      } catch (err) {
        console.error('Failed to load researchers:', err);
      }
    };
    fetchResearchers();
  }, [token]);

  // Fetch sandbox personas when filter changes
  useEffect(() => {
    if (selectedFilter === 'none') {
      setSandboxPersonas([]);
      return;
    }

    const fetchPersonas = async () => {
      setIsLoading(true);
      setError('');
      try {
        const options = { personaType };
        if (selectedFilter !== 'all') {
          options.researcherId = selectedFilter;
        }
        const data = await researcherApi.listAllSandboxPersonas(options);
        setSandboxPersonas(data || []);
      } catch (err) {
        setError('Failed to load sandbox personas.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPersonas();
  }, [selectedFilter, personaType]);

  const handleFilterChange = (e) => {
    setSelectedFilter(e.target.value);
    setExpandedId(null);
    setCloneResult(null);
    setCloneForm(null);
  };

  const openCloneForm = (persona) => {
    const uniqueSuffix = Date.now().toString(36).slice(-5);
    setCloneForm({
      personaId: persona._id,
      key: (persona.key || '') + '_sb_' + uniqueSuffix,
      name: (persona.name || '') + ' (from Sandbox)',
      persona
    });
    setCloneResult(null);
  };

  const handleConfirmClone = async () => {
    if (!cloneForm) return;
    const { persona, key, name } = cloneForm;
    setCloningId(persona._id);
    setCloneResult(null);
    try {
      await onCloneSandbox({ ...persona, key, name });
      setCloneResult({ id: persona._id, success: true, message: `"${name}" cloned to live!` });
      setCloneForm(null);
    } catch (err) {
      setCloneResult({ id: persona._id, success: false, message: err.message || 'Clone failed.' });
    } finally {
      setCloningId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div style={{
      marginBottom: '15px',
      padding: '10px',
      backgroundColor: '#fff8e1',
      border: '1px solid #ffe082',
      borderRadius: '4px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <label htmlFor="sandbox-filter" style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
          Researcher Sandbox Personas:
        </label>
        <select
          id="sandbox-filter"
          value={selectedFilter}
          onChange={handleFilterChange}
          style={{ padding: '5px 10px' }}
        >
          <option value="none">None</option>
          <option value="all">All Researchers</option>
          {researchers.map(r => (
            <option key={r._id} value={r._id}>{r.name}</option>
          ))}
        </select>
        {isLoading && <span style={{ color: '#666', fontSize: '13px' }}>Loading...</span>}
      </div>

      {error && <p style={{ color: 'red', margin: '8px 0 0' }}>{error}</p>}

      {selectedFilter !== 'none' && !isLoading && sandboxPersonas.length === 0 && !error && (
        <p style={{ color: '#666', margin: '8px 0 0', fontSize: '13px' }}>
          No sandbox {personaType} personas found.
        </p>
      )}

      {sandboxPersonas.length > 0 && (
        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {sandboxPersonas.map(persona => {
            const isPromoted = !!persona.promotedToLive?.promotedAt;
            const isExpanded = expandedId === persona._id;
            const isCloning = cloningId === persona._id;
            const result = cloneResult?.id === persona._id ? cloneResult : null;
            const isEditing = cloneForm?.personaId === persona._id;

            return (
              <div
                key={persona._id}
                style={{
                  padding: '10px',
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    color: 'white',
                    backgroundColor: isPromoted ? '#4caf50' : '#ff9800'
                  }}>
                    {isPromoted ? 'Promoted' : 'Sandbox'}
                  </span>
                  <strong>{persona.name}</strong>
                  <span style={{ color: '#666', fontSize: '13px' }}>({persona.key})</span>
                </div>

                <div style={{ marginTop: '4px', fontSize: '13px', color: '#555' }}>
                  Researcher: {persona.createdBy?.name || 'Unknown'}
                  {persona.createdAt && <span> | Created: {formatDate(persona.createdAt)}</span>}
                </div>

                {persona.description && (
                  <div style={{ marginTop: '4px', fontSize: '13px', color: '#666' }}>
                    {persona.description.length > 120
                      ? persona.description.substring(0, 120) + '...'
                      : persona.description}
                  </div>
                )}

                <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : persona._id)}
                    style={{
                      padding: '3px 10px',
                      fontSize: '12px',
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                  >
                    {isExpanded ? 'Hide Prompt' : 'View Prompt'}
                  </button>
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={() => openCloneForm(persona)}
                      disabled={isCloning}
                      style={{
                        padding: '3px 10px',
                        fontSize: '12px',
                        backgroundColor: '#ff9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      Clone to Live
                    </button>
                  )}
                  {result && (
                    <span style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: result.success ? '#4caf50' : '#f44336'
                    }}>
                      {result.message}
                    </span>
                  )}
                </div>

                {/* Inline clone form */}
                {isEditing && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: '3px'
                  }}>
                    <div style={{ marginBottom: '6px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>
                        Live Persona Key:
                      </label>
                      <input
                        type="text"
                        value={cloneForm.key}
                        onChange={(e) => setCloneForm({ ...cloneForm, key: e.target.value })}
                        style={{ width: '100%', padding: '4px 6px', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>
                        Live Persona Name:
                      </label>
                      <input
                        type="text"
                        value={cloneForm.name}
                        onChange={(e) => setCloneForm({ ...cloneForm, name: e.target.value })}
                        style={{ width: '100%', padding: '4px 6px', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={handleConfirmClone}
                        disabled={isCloning || !cloneForm.key || !cloneForm.name}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          backgroundColor: isCloning ? '#ccc' : '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: isCloning ? 'default' : 'pointer'
                        }}
                      >
                        {isCloning ? 'Saving...' : 'Confirm Clone'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setCloneForm(null)}
                        disabled={isCloning}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {isExpanded && persona.system_prompt && (
                  <pre style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                    borderRadius: '3px',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}>
                    {persona.system_prompt}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SandboxPersonaFilter;
